import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import _, { capitalize, isEmpty, map } from "lodash";
import { AlertData } from "@/pages/deals/types";
import EmptyAlerts from "@/components/layouts/FromDreamer/EmptyAlerts";
import Alert from "@/pages/deals/components/KanbanBoard/DealCard/Alert";
import OptionsDropdownMenu from "@/components/controls/OptionsDropdownMenu/OptionsDropdownMenu";
import CounterBadge from "@/components/ui/counter-badge";
import { Button } from "@/components/ui/button";

type SortOrder = "newest" | "oldest";

interface Props {
  tPrefix: string;
  dealId: string;
  alerts: AlertData[];
}

const DealAlerts: React.FC<Props> = ({ tPrefix, dealId, alerts }) => {
  const { t } = useTranslation();
  const [allAlerts, setAllAlerts] = useState(alerts);
  const [isOpenAlerts, setIsOpenAlerts] = useState(true);
  const [sortOrder, setSortOrder] = useState<SortOrder>("newest");
  const [showAllAlerts, setShowAllAlerts] = useState(false);

  useEffect(() => {
    setAllAlerts(alerts);
  }, [alerts]);

  const displayedAlerts = useMemo(() => {
    const openOrClosedAlerts = allAlerts.filter((alert) =>
      isOpenAlerts ? !alert.isDismissed : alert.isDismissed
    );
    const sortedAlerts = openOrClosedAlerts.sort((a, b) => {
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();

      return sortOrder === "newest" ? dateB - dateA : dateA - dateB;
    });

    return sortedAlerts;
  }, [allAlerts, isOpenAlerts, sortOrder]);

  const onAlertUpdate = (newDealAlerts: AlertData[]) => {
    setAllAlerts(newDealAlerts);
  };

  const alertsToShow = showAllAlerts
    ? displayedAlerts
    : displayedAlerts.slice(0, 2);
  const hasMoreAlerts = displayedAlerts.length > 2;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex w-full pl-2 justify-between">
        <div className="flex gap-2 items-center">
          <Text variant="bodySecondary" className="text-h2">
            {capitalize(
              t(`${tPrefix}.openOrClosedAlerts`, {
                openOrClosed: isOpenAlerts ? "open" : "closed"
              })
            )}
          </Text>
          {displayedAlerts.length > 0 && (
            <CounterBadge count={displayedAlerts.length} />
          )}
        </div>
        <OptionsDropdownMenu
          topOptions={{
            open: {
              action: () => setIsOpenAlerts(true),
              selected: isOpenAlerts
            },
            closed: {
              action: () => setIsOpenAlerts(false),
              selected: !isOpenAlerts
            }
          }}
          options={{
            newest: {
              action: () => setSortOrder("newest"),
              selected: sortOrder === "newest"
            },
            oldest: {
              action: () => setSortOrder("oldest"),
              selected: sortOrder === "oldest"
            }
          }}
          tPrefix={tPrefix}
          triggerIcon="preferences"
          buttonVariant="iconNoBg"
          iconSize="md"
          iconColor="secondary"
          tooltipText={t(`${tPrefix}.displayOptions`)}
          topOptionsTitle={t(`${tPrefix}.showAlerts`)}
          optionsTitle={t(`${tPrefix}.sortBy`)}
        />
      </div>
      {isEmpty(displayedAlerts) ? (
        <EmptyAlerts tPrefix={tPrefix} isOpenAlerts={isOpenAlerts} />
      ) : (
        <div className="flex flex-col gap-2">
          <div
            className={`flex flex-col gap-2 ${
              showAllAlerts && hasMoreAlerts ? "max-h-96 overflow-y-auto" : ""
            }`}
          >
            {map(alertsToShow, (alert) => (
              <Alert
                key={alert.id}
                alert={alert}
                dealId={dealId}
                onAlertUpdate={onAlertUpdate}
                isLastAlert={displayedAlerts.length === 1}
              />
            ))}
          </div>
          {hasMoreAlerts && !showAllAlerts && (
            <Button
              variant="tertiary"
              className="w-fit h-fit self-end"
              onClick={() => setShowAllAlerts(true)}
            >
              {t(`showMore`)}
            </Button>
          )}
          {hasMoreAlerts && showAllAlerts && (
            <Button
              variant="tertiary"
              className="w-fit h-fit self-end"
              onClick={() => setShowAllAlerts(false)}
            >
              {t(`showLess`)}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default DealAlerts;
